import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastTypes, GlobalToastTypeNames } from 'types';
import { useSaveTeacherProfileMutation } from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

/** Fields the teacher control panel can patch through `setTeacherProfile`. */
export interface ITeacherProfileInput {
  /** 1-5 stars; pass 0 to clear the rating. */
  rating?: number;
  internalNote?: string;
  interviewRecording?: string;
  coursesCanTeachIds?: string[];
}

interface ITeacherProfileHook {
  /** Patch one or more profile fields, then run `onDone` (refetch). */
  save: (input: ITeacherProfileInput) => Promise<boolean>;
  loading: boolean;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Wraps the `setTeacherProfile` mutation with success/error toasts and an
 * `onDone` refetch callback. Each profile widget (rating stars, internal note,
 * courses-can-teach multiselect, interview recording) on the teacher card calls
 * `save` with only the field it owns; the backend leaves unspecified fields
 * untouched.
 */
export const useTeacherProfile = (
  teacherId: string,
  onDone?: () => void,
): ITeacherProfileHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [mutate, { loading }] = useSaveTeacherProfileMutation();

  const save = useCallback(
    async (input: ITeacherProfileInput): Promise<boolean> => {
      try {
        const res = await mutate({
          variables: { teacherId, ...input },
        });
        const payload = res.data?.setTeacherProfile;
        if (payload?.success) {
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('pages.teacherDetail.profile.saved'),
            },
            type: GlobalToastTypeNames.TeacherAdd,
          });
          if (onDone) onDone();
          return true;
        }
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.teacherDetail.profile.saveFailed'),
            text: errText(payload?.customErrors),
          },
          type: GlobalToastTypeNames.TeacherAdd,
        });
        return false;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.teacherDetail.profile.saveFailed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.TeacherAdd,
        });
        return false;
      }
    },
    [mutate, teacherId, addToast, t, onDone],
  );

  return { save, loading };
};
